import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../common/components/Button";
import { toast } from "../../../common/components/Toast";
import { useAsset } from "../../api/asset";
import GeneralDetailsStep from "./GeneralDetailsStep";
import MediaStep from "./MediaStep";
import StepSidebar from "./StepSidebar";

const EditAssetModal = ({ open, onClose, assetId, onAssetUpdated }) => {
  const { t } = useTranslation("assets");
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [currentStep, setCurrentStep] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const { asset, updateAsset } = useAsset(assetId);

  const steps = [
    { title: t("editModal.generalDetails") },
    { title: t("editModal.media") },
  ];

  useEffect(() => {
    if (asset && open) {
      setTitle(asset.title || "");
      // Handle "null" string and empty/null values
      const validDescription =
        asset.description &&
        asset.description !== "null" &&
        asset.description !== "None" &&
        asset.description.trim() !== ""
          ? asset.description
          : "";
      setDescription(validDescription);
      setThumbnail(asset.thumbnail);
      setPreviewImage(null);
      setImageFile(null);
      setCurrentStep(0);
    }
  }, [asset, open]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);

      if (imageFile) {
        formData.append("thumbnail", imageFile);
      }

      await updateAsset(formData, assetId);
      toast.success(t("editModal.success"));
      onAssetUpdated?.();
      onClose();
    } catch (error) {
      toast.error(error.message || t("editModal.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return title.trim().length > 0;
      case 1:
        return true; // Media step is always valid (thumbnail is optional)
      default:
        return true;
    }
  };

  const canProceed = isStepValid(currentStep);
  const isLastStep = currentStep === steps.length - 1;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <GeneralDetailsStep
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            loading={loading}
          />
        );
      case 1:
        return (
          <MediaStep
            thumbnail={thumbnail}
            previewImage={previewImage}
            handleImageChange={handleImageChange}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          height: { xs: "auto", sm: "600px" },
          maxHeight: "90vh",
        },
      }}
    >
      <Box sx={{ display: "flex", height: "100%" }}>
        {/* Left Sidebar - Steps (hidden on mobile) */}
        <Box sx={{ display: { xs: "none", sm: "flex" } }}>
          <StepSidebar
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        </Box>

        {/* Right Content Area */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Header with Close Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              {steps[currentStep]?.title}
            </Typography>
            <IconButton onClick={handleClose} disabled={loading} size="small">
              <X size={20} />
            </IconButton>
          </Box>

          {/* Content */}
          <DialogContent sx={{ flex: 1, p: 3 }}>
            {renderStepContent()}
          </DialogContent>

          {/* Footer with Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              {t("editModal.cancel")}
            </Button>

            <Box sx={{ display: "flex", gap: 1 }}>
              {currentStep > 0 && (
                <Button
                  onClick={handlePrevious}
                  disabled={loading}
                  variant="secondary"
                >
                  {t("editModal.previous")}
                </Button>
              )}

              {!isLastStep ? (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed || loading}
                  variant="contained"
                >
                  {t("editModal.continue")}
                </Button>
              ) : (
                <Button
                  onClick={handleSave}
                  disabled={!canProceed || loading}
                  variant="contained"
                >
                  {loading ? t("editModal.saving") : t("editModal.save")}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditAssetModal;
